import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import TransportInfo from "components/TransportComponents/TransportInfo";
import { TransportService } from "service/TransportService";
import { useTransportKinds } from "hooks/useTransportKinds";
import { EMPLOYEE_ROLES } from "constants/user";
import "./parkTable.scss";

const EMPTY_VALUE = "-";

/**
 * Вид т/с — полуприцеп / сцепка / грузовик. В списке приходит либо связь
 * `transport_bodytype_additional`, либо только её id — тогда берём название из справочника.
 */
const getVehicleKind = (transport: any, kinds: Record<string, string>): string =>
    transport.transport_bodytype_additional?.value ||
    kinds[transport.transport_bodytype_additionals_id] ||
    EMPTY_VALUE;

/** Тип т/с — типы кузова, их может быть несколько */
const getVehicleBodyTypes = (transport: any): string => {
    const values = (transport.transport_bodytypes || []).map((item: any) => item.value).filter(Boolean);
    return values.length > 0 ? values.join(", ") : EMPTY_VALUE;
};

const ParkTable = ({ data, update, onAdd }: { data: any; update: any; onAdd?: () => void }) => {
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState({});
    const currentLang = useSelector((state: any) => state.settings.lang);
    const user = useSelector((state: any) => state.profile.profileData);
    const isOwner = user.employee_company_roles === EMPLOYEE_ROLES.OWNER;
    const kinds = useTransportKinds();

    const { t } = useTranslation();

    const onRowClick = useCallback(
        (id: any) => () => {
            if (!isPopupOpen) {
                TransportService.getTrackParkTransportData(id, currentLang)
                    .then((res) => {
                        setPopupData(res.data);
                        setPopupOpen(true);
                    })
                    .catch((e) => {
                        console.log(e.response);
                    });
            }
        },
        [currentLang, isPopupOpen],
    );

    const popupClose = () => {
        setPopupOpen(false);
    };

    const handleDelete = (id: any) => {
        TransportService.deleteTransportTrackpark(id).then(() => {
            setPopupOpen(false);
            setPopupData({});
            update(0);
        });
    };

    return (
        <>
            <table className="data-table park" cellSpacing="0">
                <thead className="data-table__table-header">
                    <tr>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.vehicle")}
                        </th>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.reg_number")}
                        </th>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.view")}
                        </th>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.type")}
                        </th>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.capacity")}
                        </th>
                        <th className="data-table__head data-table__head--park" align="left">
                            {t("park.vehicle_menu_headings.monitoring")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data.items.length === 0 && (
                        <tr>
                            <td colSpan={6}>
                                <div className="park__empty">
                                    <p className="park__empty-message">{t("park.if_no_vehicle_text")}</p>
                                    {onAdd && (
                                        <button type="button" className="park__empty-add" onClick={onAdd}>
                                            + {t("park.add_link")}
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    )}
                    {data.items.length > 0 && data.items.map((el: any) => (
                        <tr
                            className="data-table__table-row data-table__table-column"
                            key={"park" + el.id}
                            onClick={onRowClick(el.id)}
                        >
                            <td className="data-table__cell">
                                <div className="data-table__cell-data">
                                   <span className="park__headings">{t("park.vehicle_menu_headings.vehicle")}</span>
                                    <p className="data-table__info-container">
                                        {el.own_name || el.country || "-"}
                                    </p>
                                </div>
                            </td>
                            <td className="data-table__cell">
                                <div className="data-table__cell-data">
                                   <span className="park__headings">{t("park.vehicle_menu_headings.reg_number")}</span>
                                    <p className="data-table__info-container">
                                        {el.registration_number || "-"}
                                    </p>
                                </div>
                            </td>
                            <td className="data-table__cell">
                                <div className="data-table__cell-data">
                                   <span className="park__headings">{t("park.vehicle_menu_headings.view")}</span>
                                    <p className="data-table__info-container">
                                        {getVehicleKind(el, kinds)}
                                    </p>
                                </div>
                            </td>
                            <td className="data-table__cell">
                                <div className="data-table__cell-data">
                                   <span className="park__headings">{t("park.vehicle_menu_headings.type")}</span>
                                    <p className="data-table__info-container">
                                        {getVehicleBodyTypes(el)}
                                    </p>
                                </div>
                            </td>
                            <td className="data-table__cell">
                              <div className="data-table__cell-data">
                               <span className="park__headings">{t("park.vehicle_menu_headings.capacity")}</span>
                                <p className="data-table__info-container">{parseFloat(el.carrying_capacity) >= 0
                                    ? `${el.carrying_capacity} ${t("units.tons")}`
                                    : "-"}
                                  </p>
                                </div>
                            </td>
                            <td className="data-table__cell">
                              <div className="data-table__cell-data">
                               <span className="park__headings">{t("park.vehicle_menu_headings.monitoring")}</span>
                                <p className="data-table__info-container">
                                    {t(el.satellite_monitoring ? "yes" : "no")}
                                </p>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {onAdd && data.items.length > 0 && (
                        <tr>
                            <td colSpan={6}>
                                <div className="park__add-row">
                                    <button type="button" className="park__empty-add" onClick={onAdd}>
                                        + {t("park.add_link")}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <TransportInfo
                onClosePopup={popupClose}
                transport={popupData}
                deleteTransport={handleDelete}
                isOpen={isPopupOpen}
            />
        </>
    );
};

export default ParkTable;
